import React, { useEffect, useState } from "react"
import { useSelector } from "react-redux"

import { ASC, DESC, IDLE, PENDING, INFINITE } from "const"
import { StoreType } from "client/store"
import { OrderType, scrollToTop } from "client/utils"
import {
    Empty,
    DeleteButton,
    DeleteConfirm,
    StatusSwitcher,
    StatusType,
    PlusButton,
    MinusButton,
} from "client/components"
import {
    TableWrapper,
    TableTitle,
    TableScroller,
    TableContentWrapper,
    TableHeaderCell,
    TableCell,
    TableCellContentWrapper,
    TableCellContent,
    TableRow,
    TableMenu,
    TableBottom,
    TableBottomContent,
    TableSelected,
    TableTotal,
    SortControl,
    RowSizeType,
    CellType,
    Infinite,
} from "./styled"
import { Pagination } from "./pagination"


interface CellWidthType { [key: string]: string }
const cellWidth: CellWidthType = {
    id: "60px",
    logo: "64px",
    status: "90px",
    delete: "46px",
    sort: "36px",
    createdAt: "200px",
    updatedAt: "200px",
}

const deleteRowTimeout = 400

interface DragObjectType {
    elem: Element
    avatar?: HTMLElement
    downX: number
    downY: number
    startIndex?: number
    endIndex?: number
    position?: number
}
let dragObject: DragObjectType = null
let dragDataIds: readonly number[] = null


export type SortRows = (column: string, order: OrderType) => void

export type DeleteRowResponse = (success: boolean, callback: () => void) => void
export type DeleteRow = (item: any, response: DeleteRowResponse) => void

export type ChangeStatusResponse = (callback: () => void) => void
export type ChangeStatus = (item: any, response: ChangeStatusResponse) => void

export type DragnDrop = (dataIds: readonly number[], callback: (success: boolean) => void) => void

export type OnGetDisplayedIds = (displayedIds: number[]) => void

export interface TableColumn extends CellType {
    field: string
    header?: string
    headerFormatter?: () => any
    display?: boolean
    sortable?: boolean
    collapsible?: boolean // defines column that will be used to collapse nested rows
    showCollapseButton?: boolean
    style?: React.CSSProperties
    formatter?: (item: any) => any
    editAccess?: (item: any) => boolean
    deleteAccess?: (item: any) => boolean
    onClick?: (item: any, e?: React.MouseEvent<HTMLDivElement, MouseEvent>) => void
}

export interface TableColumnEditable extends TableColumn {
    focusedFormatter?: (item: any) => any
}

export interface TableNestedIds {
    id: number | typeof INFINITE
    nestedIds: readonly TableNestedIds[]
}
export interface TableGroupedIds {
    groupId: number // 0 or NULL - top level for no grouping
    groupData?: any
    level?: 1 | 2 // for styling
    nestedIds: readonly TableNestedIds[]
    nestedGroupedIds: readonly TableGroupedIds[]
}
export interface TableCustomLevelViewProps {
    groupData: any
}

export interface TableProps extends React.HTMLAttributes<HTMLDivElement> {
    title?: string
    className?: string
    columns: TableColumn[]
    dataList: { [key: number]: any }
    dataIds?: readonly number[]
    dataGroupedIds?: readonly TableGroupedIds[]
    CustomViewLevel1?: React.ElementType
    CustomViewLevel2?: React.ElementType
    collapsedGroupes?: boolean // collapse top level groupes by default
    expandedItems?: boolean // expand all items by default
    onGetDisplayedIds?: OnGetDisplayedIds
    selectedIds?: readonly number[]
    sortColumn?: string
    sortOrder?: OrderType
    rowSize?: keyof RowSizeType
    showPagination?: boolean
    showScroll?: boolean
    showTitle?: boolean
    showBottom?: boolean
    showTotal?: boolean
    bottomContent?: any
    scrollToTopOnPagination?: boolean
    onSort?: SortRows
    onDelete?: DeleteRow
    onStatusChange?: ChangeStatus
    onDragnDrop?: DragnDrop
}

export const Table = ({
    title,
    className = null,
    columns = [],
    dataList = {},
    dataIds = null,
    dataGroupedIds = null,
    CustomViewLevel1 = null,
    CustomViewLevel2 = null,
    collapsedGroupes = false,
    expandedItems = false,
    onGetDisplayedIds,
    selectedIds = [],
    sortColumn,
    sortOrder,
    rowSize = "sm",
    showPagination = true,
    showScroll = true,
    showTitle = true,
    showBottom = true,
    showTotal = true,
    bottomContent,
    scrollToTopOnPagination = true,
    onSort,
    onDelete,
    onStatusChange,
    onDragnDrop,
    ...other
}: TableProps) => {
    const [curPage, setCurPage] = useState(1)
    const [itemExpanded, setItemExpanded] = useState<string[]>([])
    const [groupCollapsed1, setGroupCollapsed1] = useState<number[]>([])
    const [groupCollapsed2, setGroupCollapsed2] = useState<number[]>([])
    const [confirmDeleteId, setConfirmDeleteId] = useState<number | string>(null)
    const [confirmTarget, setConfirmTarget] = useState<HTMLElement>(null)
    const [confirmShow, setConfirmShow] = useState(false)
    const [removingId, setRemovingId] = useState<number | string>(null)
    const [removedId, setRemovedId] = useState<number | string>(null)
    const [statusId, setStatusId] = useState<number | string>(null)

    const rowsPerPage = useSelector((state: StoreType) => state.system.userSettings.system.rowsPerPage)
    const lang = useSelector((state: StoreType) => state.system.lang)

    const displayedDataIds: number[] = []

    useEffect(() => {
        // collapse top level
        if (collapsedGroupes) {
            const collapsed: number[] = []
            dataGroupedIds.forEach((dataGrouped) => {
                collapsed.push(dataGrouped.groupId)
            })
            setGroupCollapsed1(collapsed)
        }
    }, [collapsedGroupes, dataGroupedIds])

    useEffect(() => {
        // expand items
        if (expandedItems) {
            const expanded: string[] = []

            const expandItems = (items: readonly TableNestedIds[], parentHistory: number[]) => {
                items.forEach((item) => {
                    const itemId = Number(item.id)
                    const newParentHistory = parentHistory.slice()
                    newParentHistory.push(itemId)

                    const rowIndex = `item_${newParentHistory.join("_")}`

                    expanded.push(rowIndex)
                    expandItems(item.nestedIds, newParentHistory)
                })
            }

            const expandGroups = (groups: readonly TableGroupedIds[]) => {
                groups.forEach((group) => {
                    expandItems(group.nestedIds, [])
                    expandGroups(group.nestedGroupedIds)
                })
            }

            expandGroups(dataGroupedIds)
            setItemExpanded(expanded)
        }
    }, [expandedItems, dataGroupedIds])

    useEffect(() => {
        if (onGetDisplayedIds) {
            onGetDisplayedIds(displayedDataIds)
        }
    }, [dataGroupedIds, itemExpanded, groupCollapsed1, groupCollapsed2])

    // count the whole length
    let dataLength = 0
    if (dataIds && dataIds.length) {
        dataLength = dataIds.length
    }
    if (dataGroupedIds) {
        dataGroupedIds.forEach((dataGrouped) => {
            dataLength += dataGrouped.nestedIds.length
            dataLength += dataGrouped.nestedGroupedIds.length
        })
    }

    // current page and data indexes for it
    const numberOfPages = dataLength ? Math.ceil(dataLength / rowsPerPage) : 1
    if (numberOfPages < curPage) setCurPage(numberOfPages)
    if (curPage < 1) setCurPage(1)

    const indexStart = (curPage - 1) * rowsPerPage
    const indexEnd = indexStart + rowsPerPage


    // display data
    let displayIds: readonly number[] = []
    let displayGroupedIds: TableGroupedIds[] = []
    if (dataIds) {
        displayIds = showPagination
            ? dataIds.slice(indexStart, indexEnd + 1) // last row is hidden for smooth delete animation
            : dataIds
    }
    if (dataGroupedIds) {
        if (!showPagination) displayGroupedIds = dataGroupedIds as TableGroupedIds[]
        else {
            const addDisplayGroup = (group: TableGroupedIds) => {
                if (
                    displayGroupedIds.length > 0
                    && displayGroupedIds[displayGroupedIds.length - 1].groupId === group.groupId
                ) return
                displayGroupedIds.push({
                    groupId: group.groupId,
                    groupData: group.groupData,
                    level: group.level,
                    nestedIds: [],
                    nestedGroupedIds: [],
                })
            }
            const currentIndexFits = (currentIndex: number) => (currentIndex >= indexStart && currentIndex < indexEnd)
            const targetIndexFits = (targetIndex: number) => (
                // start index equals "currentIndex"
                (targetIndex >= indexStart && targetIndex < indexEnd) || targetIndex >= indexEnd
            )

            let currentIndex = 0
            dataGroupedIds.forEach((group) => {
                if (currentIndexFits(currentIndex) || targetIndexFits(currentIndex + group.nestedIds.length)) {
                    addDisplayGroup(group)
                    const targetGroup = displayGroupedIds[displayGroupedIds.length - 1]

                    group.nestedIds.forEach((nested) => {
                        if (currentIndexFits(currentIndex)) {
                            const nestedIdsCopy = targetGroup.nestedIds.slice()
                            nestedIdsCopy.push(nested)
                            targetGroup.nestedIds = nestedIdsCopy
                        }
                        currentIndex += 1
                    })
                } else {
                    currentIndex += group.nestedIds.length
                }

                if (currentIndexFits(currentIndex) || targetIndexFits(currentIndex + group.nestedGroupedIds.length)) {
                    addDisplayGroup(group)
                    const targetGroup = displayGroupedIds[displayGroupedIds.length - 1]

                    group.nestedGroupedIds.forEach((nestedGroup) => {
                        if (currentIndexFits(currentIndex)) {
                            const nestedGroupedIdsCopy = targetGroup.nestedGroupedIds.slice()
                            nestedGroupedIdsCopy.push(nestedGroup)
                            targetGroup.nestedGroupedIds = nestedGroupedIdsCopy
                        }
                        currentIndex += 1
                    })
                } else {
                    currentIndex += group.nestedGroupedIds.length
                }
            })
        }
    }


    const goToPage = (page: number) => {
        if (statusId) return
        if (removingId || removedId) return

        setCurPage(page)
        setConfirmDeleteId(null)
        setConfirmShow(false)

        // clear collapsed
        setItemExpanded([])
        setGroupCollapsed1([])
        setGroupCollapsed2([])

        if (scrollToTopOnPagination) scrollToTop()
    }

    const sort = (column: string) => {
        if (statusId) return
        if (removingId || removedId) return

        let order: OrderType
        if (column === sortColumn) {
            order = (sortOrder === ASC) ? DESC : ASC
        } else order = ASC
        goToPage(1)
        onSort(column, order)
    }

    const changeStatus = (
        itemId: number | string,
        item: any,
    ) => {
        if (statusId) return
        if (removingId || removedId) return

        setStatusId(itemId)
        onStatusChange(item, (callback) => {
            setStatusId(null)
            callback()
        })
    }

    const deleteRow = (
        e: React.MouseEvent<HTMLElement>,
        itemId: number | string,
        item: any,
    ) => {
        if (statusId) return
        if (removingId || removedId) return

        if (confirmDeleteId !== itemId) {
            setConfirmDeleteId(itemId)
            setConfirmTarget(e.target as HTMLElement)
            setConfirmShow(true)
        } else {
            setConfirmShow(false)
            setConfirmDeleteId(null)
            setRemovingId(itemId)
            onDelete(item, (success, callback) => {
                if (success) {
                    setRemovingId(null)
                    setRemovedId(itemId)
                    setTimeout(() => {
                        callback()
                        setRemovedId(null)
                    }, deleteRowTimeout)
                } else {
                    setRemovingId(null)
                }
            })
        }
    }

    const closeConfirm = () => {
        setConfirmDeleteId(null)
        setConfirmShow(false)
    }

    const collapseItem = (e: React.MouseEvent<HTMLElement>, rowIndex: string) => {
        e.stopPropagation()

        let expandedCopy
        if (itemExpanded && itemExpanded.includes(rowIndex)) {
            expandedCopy = itemExpanded.filter((row) => (row !== rowIndex))
        } else {
            expandedCopy = itemExpanded ? itemExpanded.slice() : []
            expandedCopy.push(rowIndex)
        }
        setItemExpanded(expandedCopy)
    }

    const collapseGroupLevel1 = (groupId: number) => {
        let collapsedCopy
        if (groupCollapsed1 && groupCollapsed1.includes(groupId)) {
            collapsedCopy = groupCollapsed1.filter((id) => (id !== groupId))
        } else {
            collapsedCopy = groupCollapsed1 ? groupCollapsed1.slice() : []
            collapsedCopy.push(groupId)
        }
        setGroupCollapsed1(collapsedCopy)
    }

    const collapseGroupLevel2 = (groupId: number) => {
        let collapsedCopy
        if (groupCollapsed2 && groupCollapsed2.includes(groupId)) {
            collapsedCopy = groupCollapsed2.filter((id) => (id !== groupId))
        } else {
            collapsedCopy = groupCollapsed2 ? groupCollapsed2.slice() : []
            collapsedCopy.push(groupId)
        }
        setGroupCollapsed2(collapsedCopy)
    }

    // drag'n'drop
    const onMouseDown = (e: React.MouseEvent<Element, MouseEvent>, startIndex: number) => {
        if (e.button !== 0) return // not left button is pressed
        const target = e.target as HTMLElement
        const elem = target.closest(TableRow)

        dragDataIds = dataIds
        dragObject = {
            elem,
            downX: e.pageX,
            downY: e.pageY,
            startIndex,
        }

        if (onDragnDrop) {
            document.body.addEventListener("mousemove", onMouseMove)
            document.body.addEventListener("mouseup", onMouseUp)
        }
    }

    const onMouseMove = (e: MouseEvent) => {
        if (!dragObject) return // no element

        if (!dragObject.avatar) { // create avatar and start moving
            // prevent tiny movement
            const moveX = e.pageX - dragObject.downX
            const moveY = e.pageY - dragObject.downY
            if (Math.abs(moveX) < 3 && Math.abs(moveY) < 3) return

            const elem = dragObject.elem as HTMLElement
            const width = elem.offsetWidth
            const height = elem.offsetHeight
            dragObject.avatar = elem.cloneNode(true) as HTMLElement
            dragObject.avatar.classList.add("drag-item")
            dragObject.avatar.style.width = `${width}px`
            dragObject.avatar.style.height = `${height}px`

            document.body.append(dragObject.avatar)
            document.body.classList.add("dragging")
            dragObject.elem.classList.add("ghost")
        }

        const top = e.pageY - 16
        const left = e.pageX - 17
        dragObject.avatar.style.left = `${left}px`
        dragObject.avatar.style.top = `${top}px`
    }

    const onMouseEnter = (e: React.MouseEvent<Element, MouseEvent>, endIndex: number) => {
        if (!dragObject) return // no element
        const { startIndex } = dragObject

        if (endIndex !== startIndex) {
            dragObject.endIndex = endIndex

            const target = e.target as Element
            const elem = target.closest(TableRow)
            if (elem.previousElementSibling !== dragObject.elem) {
                elem.before(dragObject.elem)
                dragObject.position = -1 // before
            } else {
                elem.after(dragObject.elem)
                dragObject.position = 1 // after
            }
        }
    }

    const onMouseUp = () => {
        if (!dragObject || !dragDataIds) return // no element
        const { elem, startIndex, endIndex, position } = dragObject

        if (startIndex >= 0 && endIndex >= 0) {
            const resultIds: number[] = []
            const moveItem = dragDataIds[startIndex]
            dragDataIds.forEach((id, index) => {
                if (index !== startIndex && index !== endIndex) resultIds.push(id)
                if (index === endIndex && position === -1) { // insert before
                    resultIds.push(moveItem)
                    resultIds.push(id)
                }
                if (index === endIndex && position === 1) { // insert after
                    resultIds.push(id)
                    resultIds.push(moveItem)
                }
            })

            if (onDragnDrop) {
                elem.closest(TableWrapper).classList.add("disabled")
                onDragnDrop(resultIds, () => {
                    elem.closest(TableWrapper).classList.remove("disabled")
                })
            }
        }

        clearDragObject()
    }

    const clearDragObject = () => {
        if (!dragObject) return // no element
        if (dragObject.avatar) dragObject.avatar.remove()

        const elem = dragObject.elem as HTMLElement
        elem.classList.remove("ghost")

        dragObject = null
        dragDataIds = null
        document.body.classList.remove("dragging")

        document.body.removeEventListener("mousemove", onMouseMove)
        document.body.removeEventListener("mouseup", onMouseUp)
    }


    // creating rows for nested table
    const nestedRowsArray: any[] = []

    const nestedItemsToDisplay = (nested: readonly TableNestedIds[], nestedLevel: number, parentHistory: number[]) => {
        nested.forEach((nestedRow) => {
            const infinite = nestedRow.id === INFINITE
            const itemId = Number(nestedRow.id)
            const item = dataList ? dataList[itemId] : null

            const nestedStyle: React.CSSProperties = {
                flexDirection: "row",
                justifyContent: "left",
                alignItems: "center",
                paddingLeft: `${nestedLevel * 30 + 12}px`,
            }

            if (infinite) {
                nestedRowsArray.push(
                    <TableRow key={`infinite_${parentHistory.join("_")}`} size="sm">
                        <TableCell colSpan={100}>
                            <TableCellContentWrapper style={nestedStyle}>
                                <Infinite />
                            </TableCellContentWrapper>
                        </TableCell>
                    </TableRow>,
                )
            } else if (item) {
                const newParentHistory = parentHistory.slice()
                newParentHistory.push(itemId)

                const rowIndex = `item_${newParentHistory.join("_")}`
                const hidden = removedId === rowIndex
                const isExpanded = itemExpanded.includes(rowIndex)

                displayedDataIds.push(itemId)
                nestedRowsArray.push(
                    <Row
                        key={rowIndex}
                        rowIndex={rowIndex}
                        rowSize={rowSize}
                        rowSelected={selectedIds && selectedIds.includes(itemId)}
                        rowHidden={hidden}
                        transition={removedId ? deleteRowTimeout : null}
                        columns={columns}
                        item={item}
                        hasNested={nestedRow.nestedIds.length > 0}
                        nestedStyle={nestedStyle}
                        isCollapsed={!isExpanded}
                        onCollapse={(e) => collapseItem(e, rowIndex)}
                        removingId={removingId}
                        statusId={statusId}
                        onDelete={(e) => onDelete && deleteRow(e, rowIndex, item)}
                        onStatusChange={() => onStatusChange && changeStatus(rowIndex, item)}
                    />,
                )
                if (isExpanded) nestedItemsToDisplay(nestedRow.nestedIds, nestedLevel + 1, newParentHistory)
            }
        })
    }
    const nestedGroupsToDisplay = (nested: readonly TableGroupedIds[]) => {
        nested.forEach((group) => {
            const {
                groupId,
                level,
                nestedIds,
                nestedGroupedIds,
            } = group
            let isCollapsed = false
            if (level === 1) isCollapsed = groupCollapsed1.includes(groupId)
            if (level === 2) isCollapsed = groupCollapsed2.includes(groupId)

            if (groupId) { // 0 or NULL - for no grouping
                let groupContent = group.groupData
                if (CustomViewLevel1 && level === 1) groupContent = <CustomViewLevel1 groupData={group.groupData} />
                if (CustomViewLevel2 && level === 2) groupContent = <CustomViewLevel2 groupData={group.groupData} />

                nestedRowsArray.push(
                    // use "overflowVisible" to display dropdowns
                    <TableRow
                        key={`level_${level}_group_${groupId}`}
                        size={rowSize}
                        sectionLevel={level}
                    >
                        <TableCell
                            colSpan={100} // colspan all columns
                            overflowVisible // to display dropdowns
                            onClick={() => {
                                if (level === 1) collapseGroupLevel1(groupId)
                                if (level === 2) collapseGroupLevel2(groupId)
                            }}
                        >
                            <TableCellContentWrapper overflowVisible>
                                { isCollapsed ? <PlusButton /> : <MinusButton /> }
                                <TableCellContent overflowVisible fitContent={rowSize === "fit"}>
                                    {groupContent}
                                </TableCellContent>
                            </TableCellContentWrapper>
                        </TableCell>
                    </TableRow>,
                )
            }

            if (!isCollapsed) {
                nestedItemsToDisplay(nestedIds, 0, [])
                nestedGroupsToDisplay(nestedGroupedIds)
            }
        })
    }

    nestedGroupsToDisplay(displayGroupedIds)

    return (
        <TableWrapper className={className} {...other}>
            { title && <TableTitle>{title}</TableTitle> }
            <TableScroller showScroll={showScroll}>
                <TableContentWrapper>
                    {
                        showTitle && (
                            <thead>
                                <TableRow size="sm" noHover>
                                    {
                                        columns.map((column) => column.display !== false
                                        && (
                                            <TableHeaderCell
                                                key={column.field}
                                                cellWidth={column.cellWidth || cellWidth[column.field] || null}
                                                cellMinWidth={column.cellMinWidth || cellWidth[column.field] || null}
                                                cellMaxWidth={column.cellMaxWidth || cellWidth[column.field] || null}
                                                onClick={() => column.sortable && onSort && sort(column.field)}
                                            >
                                                {
                                                    column.header
                                                    && <span>{column.header}</span>
                                                }
                                                {
                                                    column.headerFormatter
                                                    && column.headerFormatter()
                                                }
                                                {
                                                    column.sortable
                                                    && (
                                                        <SortControl
                                                            show={sortColumn === column.field}
                                                            direction={sortColumn === column.field ? sortOrder : DESC}
                                                        />
                                                    )
                                                }
                                            </TableHeaderCell>
                                        ))
                                    }
                                </TableRow>
                            </thead>
                        )
                    }
                    <tbody>
                        {
                            displayIds.map((id, index) => {
                                const item = dataList ? dataList[id] : null
                                let hidden = false
                                if (removedId === id) hidden = true
                                if (
                                    showPagination
                                    && displayIds.length > rowsPerPage
                                    && index === displayIds.length - 1
                                ) hidden = removedId === null

                                if (!item) return null
                                return (
                                    <Row
                                        key={id}
                                        rowIndex={id}
                                        rowSize={rowSize}
                                        rowSelected={selectedIds && selectedIds.includes(id)}
                                        rowHidden={hidden}
                                        transition={removedId ? deleteRowTimeout : null}
                                        columns={columns}
                                        item={item}
                                        removingId={removingId}
                                        statusId={statusId}
                                        onDelete={(e) => onDelete && deleteRow(e, id, item)}
                                        onStatusChange={() => onStatusChange && changeStatus(id, item)}
                                        onMouseDown={(e) => onMouseDown(e, index)}
                                        onMouseEnter={(e) => onMouseEnter(e, index)}
                                    />
                                )
                            })
                        }
                        {
                            nestedRowsArray
                        }
                        {
                            (
                                (dataIds && dataIds.length === 0)
                                || (dataGroupedIds && dataGroupedIds.length === 0)
                            ) && (
                                <TableRow size="sm">
                                    <TableCell colSpan={100}>
                                        <TableCellContentWrapper>
                                            <Empty />
                                        </TableCellContentWrapper>
                                    </TableCell>
                                </TableRow>
                            )
                        }
                        <DeleteConfirm
                            target={confirmTarget}
                            show={confirmShow}
                            onClose={closeConfirm}
                        />
                    </tbody>
                </TableContentWrapper>
            </TableScroller>
            {
                showBottom && (
                    <TableBottom>
                        {
                            bottomContent && (
                                <TableBottomContent>
                                    {bottomContent}
                                </TableBottomContent>
                            )
                        }
                        {
                            selectedIds && selectedIds.length > 0
                            && (
                                <TableSelected>
                                    {`${lang.selected}: ${selectedIds.length}`}
                                </TableSelected>
                            )
                        }
                        {
                            showPagination && numberOfPages > 1
                            && (
                                <Pagination
                                    curPage={curPage}
                                    numberOfPages={numberOfPages}
                                    onClickPrevPage={() => goToPage(curPage - 1)}
                                    onClickPage={(page) => goToPage(page)}
                                    onClickNextPage={() => goToPage(curPage + 1)}
                                />
                            )
                        }
                        {
                            showTotal && (
                                <TableTotal>
                                    {`${lang.total}: ${dataLength}`}
                                </TableTotal>
                            )
                        }
                    </TableBottom>
                )
            }
        </TableWrapper>
    )
}


interface RowProps {
    rowIndex: number | string
    rowSize: keyof RowSizeType
    rowSelected?: boolean
    columns: TableColumn[]
    item: any

    // removing animation
    rowHidden: boolean
    transition: number

    // for nested rows
    hasNested?: boolean
    nestedStyle?: React.CSSProperties,
    isCollapsed?: boolean
    onCollapse?: (e: React.MouseEvent<HTMLElement>) => void

    // for "status" and "delete" buttons
    removingId: number | string
    statusId: number | string
    onDelete: (e: React.MouseEvent<HTMLElement>) => void
    onStatusChange: () => void

    // drag'n'drop
    onMouseDown?: (e: React.MouseEvent<HTMLElement>) => void
    onMouseEnter?: (e: React.MouseEvent<HTMLElement>) => void
}

const Row = ({
    rowIndex,
    rowSize,
    rowSelected = false,
    columns,
    item,
    rowHidden,
    transition,
    hasNested = false,
    nestedStyle = null,
    isCollapsed = false,
    onCollapse = null,
    removingId,
    statusId,
    onDelete,
    onStatusChange,
    onMouseDown = null,
    onMouseEnter = null,
}: RowProps) => (
    <TableRow
        size={rowSize}
        rowSelected={rowSelected}
        rowHidden={rowHidden}
        transition={transition}
    >
        {
            columns.map((column) => {
                if (column.display === false) return null

                const nestedRowStyle: React.CSSProperties = column.collapsible
                    ? {
                        ...(column.style || {}),
                        ...(nestedStyle || {}),
                    }
                    : null

                const collapseButton = column.collapsible && column.showCollapseButton !== false && hasNested
                    ? (isCollapsed ? <PlusButton onClick={onCollapse} /> : <MinusButton onClick={onCollapse} />)
                    : null

                return (
                    // use "overflowVisible" to display dropdowns
                    <TableCell
                        key={column.field}
                        cellWidth={column.cellWidth || cellWidth[column.field] || null}
                        cellMinWidth={column.cellMinWidth || cellWidth[column.field] || null}
                        cellMaxWidth={column.cellMaxWidth || cellWidth[column.field] || null}
                        centerAlign={column.centerAlign}
                        overflowVisible={column.overflowVisible || false}
                        onClick={(e) => column.onClick && column.onClick(item, e)}
                    >
                        <TableCellContentWrapper
                            overflowVisible={column.overflowVisible || false}
                            style={nestedRowStyle || column.style || null}
                        >
                            {collapseButton}

                            <TableCellContent
                                overflowVisible={column.overflowVisible || false}
                                fitContent={rowSize === "fit"}
                            >
                                {
                                    column.field === "delete" && (
                                        column.deleteAccess
                                        && column.deleteAccess(item)
                                        && (
                                            <DeleteButton
                                                status={removingId === rowIndex ? PENDING : IDLE}
                                                onClick={onDelete}
                                            />
                                        )
                                    )
                                }
                                {
                                    column.field === "status" && (
                                        column.formatter
                                            ? column.formatter(item)
                                            : (
                                                <StatusSwitcher
                                                    status={statusId === rowIndex ? PENDING : item[column.field] as StatusType}
                                                    disabled={column.editAccess ? !column.editAccess(item) : true}
                                                    onClick={onStatusChange}
                                                />
                                            )
                                    )
                                }
                                {
                                    column.field === "sort" && (
                                        <TableMenu
                                            cursor="grab"
                                            onMouseDown={onMouseDown}
                                            onMouseEnter={onMouseEnter}
                                            onClick={(e) => (column.onClick ? column.onClick(item, e) : null)}
                                        />
                                    )
                                }
                                {
                                    column.field !== "delete"
                                    && column.field !== "status"
                                    && column.field !== "sort"
                                    && (
                                        column.formatter
                                            ? column.formatter(item)
                                            : item[column.field]
                                    )
                                }
                            </TableCellContent>
                        </TableCellContentWrapper>
                    </TableCell>
                )
            })
        }
    </TableRow>
)
